import { useCallback, useEffect, useState } from 'react';
import { fleetApi, type Boxes, type Queue } from './fleet-api.js';
import { fleetAlerts, type FleetAlert } from './fleet-alerts.js';
import { fleetToken, fleetTokenIfHeld, NoFleetToken } from './fleet-token.js';
import { usePoll } from './usePoll.js';

/**
 * The CI fleet's alerts, for the nav count and the CI fleet screen's banner.
 * org-admin#97. Held by the app shell, so the screen and the nav read one poll.
 *
 * ── ONLY THE CI FLEET SCREEN SIGNS IN ────────────────────────────────
 *
 * Getting a fleet-api token leaves the page for auth and comes back. That is
 * expected when an admin opens CI fleet, and would be absurd while they read
 * Users. So away from CI fleet this uses only a token the page already holds,
 * and until CI fleet has been opened there is no count, rather than a
 * round trip nobody asked for.
 *
 * ── A CHECK THAT COULD NOT RUN SAYS SO ───────────────────────────────
 *
 * If one of the reads fails, the alerts from the other still show, and
 * `unchecked` names what was not looked at. No alerts must never be how "could
 * not look" reads.
 */

export interface FleetAlertsState {
  alerts: FleetAlert[];
  /** What could not be read this time, in words. */
  unchecked: string[];
  checkedAt: number;
}

export function useFleetAlerts(onFleetScreen: boolean): FleetAlertsState | null {
  const [state, setState] = useState<FleetAlertsState | null>(null);

  const reload = useCallback(async () => {
    const api = fleetApi(onFleetScreen ? fleetToken : fleetTokenIfHeld);
    const [boxes, queue] = await Promise.allSettled([api.boxes(), api.queue()]);
    // Not signed in to fleet-api on this page yet: nothing to say, not a failure.
    if ([boxes, queue].some((r) => r.status === 'rejected' && r.reason instanceof NoFleetToken)) return;

    const value = <T,>(r: PromiseSettledResult<T>): T | null => (r.status === 'fulfilled' ? r.value : null);
    const unchecked = [
      boxes.status === 'rejected' ? 'box health' : null,
      queue.status === 'rejected' ? 'stuck jobs' : null,
    ].filter((s): s is string => s !== null);

    setState({
      alerts: fleetAlerts({ boxes: value<Boxes>(boxes), queue: value<Queue>(queue) }),
      unchecked,
      checkedAt: Date.now(),
    });
  }, [onFleetScreen]);

  useEffect(() => {
    void reload();
  }, [reload]);
  usePoll(useCallback(() => void reload(), [reload]));

  return state;
}
